import Breadcrumbs from '@storeblocks/breadcrumbs';
import { format, parseISO } from 'date-fns';
import { type FC } from 'react';
import { useParams } from 'react-router';
import { styled } from 'styled-components';

import { QueryState } from '@/api/components/QueryState';
import { useFundEsgQuery } from '@/api/esg/esgApi';
import { SfdrArticle } from '@/api/esg/models/SfdrArticle';
import { useFundListQuery } from '@/api/funds/fundsApi';
import { useOrganizationCmId } from '@/api/userSettings/hooks/useOrganizationCmId';
import { isNotReady } from '@/api/utils/isNotReady';
import { AnnouncementWarning } from '@/components/AnnouncementWarning';
import { CenteredPageContent } from '@/components/CenteredPageContent';
import { Documents } from '@/components/FundDocuments/Documents';
import { Column } from '@/components/Grid/Column';
import { Row } from '@/components/Grid/Row';
import { InternalLink } from '@/components/InternalLink';
import { MainContent } from '@/components/MainContent';
import { OrderToSignAlert } from '@/components/OrderToSignAlert/OrderToSignAlert';
import { PageHeading } from '@/components/PageHeading';
import { ConnectSurface } from '@/components/Pretzel/ConnectSurface';
import { WithGap } from '@/components/WithGap';
import { useTexts } from '@/hooks/useTexts';
import { fmsWithTemplate } from '@/i18n/fmsWithTemplate';
import { useUserDateFormat } from '@/i18n/hooks';

import { EmDash } from './components/EmDash';
import { sfdrToFms } from './utils/sfdrToFms';
import { EsgCarbonIntensities } from './widgets/EsgCarbonIntensities';
import { EsgScienceBasedTargets } from './widgets/EsgScienceBasedTargets';
import { EsgSolutions } from './widgets/EsgSolutions';

export const EsgFundPage: FC = () => {
  const texts = useTexts();

  const dateFormat = useUserDateFormat();

  const { isin } = useParams();
  const organizationCmId = useOrganizationCmId();

  const esgFundQuery = useFundEsgQuery({ isin: isin! });
  const fundListQuery = useFundListQuery();

  const sfdrArticleTexts = sfdrToFms(
    esgFundQuery.data?.sfdr || SfdrArticle.Unknown,
    texts,
  );

  if (isNotReady(esgFundQuery) || isNotReady(fundListQuery)) {
    return (
      <CenteredPageContent>
        <QueryState
          query={[esgFundQuery, fundListQuery]}
          customTexts={{ noData: { description: texts.pages.esg.noData } }}
        />
      </CenteredPageContent>
    );
  }

  return (
    <CenteredPageContent data-test="esg-fund-page">
      <AnnouncementWarning />
      <OrderToSignAlert />

      <Breadcrumbs expandAllAriaLabel={texts.breadcrumbs.expandAllAriaLabel}>
        <InternalLink to={`/${organizationCmId}`}>
          {texts.menu.links.home}
        </InternalLink>
        <InternalLink to={`/${organizationCmId}/fund/fund-list`}>
          {texts.menu.links.fundList}
        </InternalLink>
        <InternalLink
          to={`/${organizationCmId}/sustainability/esg/fund/${esgFundQuery.data!.isin}/${esgFundQuery.data!.fundName}`}
        >
          {esgFundQuery.data!.fundName}
        </InternalLink>
      </Breadcrumbs>

      <MainContent>
        <PageHeading header={esgFundQuery.data!.fundName} />

        <WithGap gap="16" direction="row">
          <div className="font-details">
            {fmsWithTemplate(texts.common.lastUpdated, {
              date: format(
                parseISO(esgFundQuery.data!.updatedDate),
                dateFormat,
              ),
            })}
          </div>

          <div className="font-details">
            <EmDash />
          </div>

          <div className="font-details">{esgFundQuery.data!.isin}</div>

          {esgFundQuery.data!.benchmarkName && (
            <>
              <div className="font-details">
                <EmDash />
              </div>

              <div className="font-details">
                {fmsWithTemplate(texts.pages.fundEsg.benchmark.name, {
                  name: esgFundQuery.data!.benchmarkName,
                })}
              </div>
            </>
          )}
        </WithGap>

        <WithGap gap="64">
          <Row>
            <Column $span={6}>
              <ConnectSurface>
                <Documents isin={esgFundQuery.data!.isin} />
              </ConnectSurface>
            </Column>

            <Column $span={6}>
              <div>
                <ConnectSurface>
                  <h2 className="font-body-demibold">
                    {texts.pages.fundEsg.sfdr.title}{' '}
                    <Capitalize>{sfdrArticleTexts.title}</Capitalize>
                  </h2>

                  <br />

                  <p className="font-body">{sfdrArticleTexts.description}</p>
                </ConnectSurface>
              </div>
            </Column>
          </Row>

          {esgFundQuery.data!.carbonIntensities.length > 0 && (
            <EsgCarbonIntensities isin={isin!} />
          )}

          <EsgSolutions isin={isin!} />

          {esgFundQuery.data!.scienceBasedTargetSet && (
            <EsgScienceBasedTargets isin={isin!} />
          )}
        </WithGap>
      </MainContent>
    </CenteredPageContent>
  );
};

const Capitalize = styled.span`
  text-transform: capitalize;
`;
